import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, FormProvider } from 'react-hook-form';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { Select } from '@/components/ui/Select';
import { Tabs, TabPanel, useTabs } from '@/components/ui/Tabs';
import { useToast } from '@/components/ui/Toast';
import { DynamicFormRenderer } from './DynamicFormRenderer';
import {
  useFormDefinitions,
  useFormDefinitionFull,
  useCreateFormDefinition,
  useDeleteFormDefinition,
  useDuplicateFormDefinition,
  type FormDefinition,
  type FormDefinitionFull,
  type EntityScope,
} from './hooks/useFormBuilder';

const SCOPE_LABELS: Record<EntityScope, string> = {
  contact: 'Contact',
  job: 'Job',
  invoice: 'Invoice',
  document: 'Document',
  time_entry: 'Time Entry',
};

/**
 * FormBuilderPage — lists all form definitions, allows creating new ones.
 */
export function FormBuilderPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [previewFormId, setPreviewFormId] = useState<string | null>(null);
  const { data: forms, isLoading } = useFormDefinitions();
  const createMutation = useCreateFormDefinition();
  const deleteMutation = useDeleteFormDefinition();
  const copyMutation = useDuplicateFormDefinition();

  const handleCreate = (data: { name: string; description: string; entity_scope: EntityScope }) => {
    createMutation.mutate(data, {
      onSuccess: (result) => {
        toast({ variant: 'success', title: 'Form created' });
        setIsCreateOpen(false);
        navigate(`/settings/form-builder/${result.id}`);
      },
    });
  };

  const handleDelete = (form: FormDefinition) => {
    if (confirm(`Delete "${form.name}"? This cannot be undone.`)) {
      deleteMutation.mutate(form.id, {
        onSuccess: () => toast({ variant: 'success', title: 'Form deleted' }),
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">Form Builder</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Create and manage dynamic forms for contacts, jobs, and other entities.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={() => navigate('/settings/form-builder/field-library')}
          >
            Field Library
          </Button>
          <Button variant="outline" onClick={() => navigate('/settings/form-builder/mappings')}>
            Entity Type Mappings
          </Button>
          <Button onClick={() => setIsCreateOpen(true)}>New Form</Button>
        </div>
      </div>

      <Card>
        <CardHeader title="Form Definitions" />
        <CardContent>
          {!forms || forms.length === 0 ? (
            <p className="py-4 text-sm text-gray-500">
              No forms defined yet. Create one to get started.
            </p>
          ) : (
            <div className="divide-y divide-border dark:divide-border-dark">
              {forms.map((form) => (
                <div key={form.id} className="flex items-center justify-between py-4">
                  <div>
                    <button
                      type="button"
                      onClick={() => navigate(`/settings/form-builder/${form.id}`)}
                      className="text-sm font-medium text-brand-600 hover:text-brand-700 dark:text-brand-400"
                    >
                      {form.name}
                    </button>
                    <div className="mt-1 flex items-center gap-2">
                      <Badge variant="info" size="sm">
                        {SCOPE_LABELS[form.entity_scope]}
                      </Badge>
                      <Badge variant={form.is_active ? 'success' : 'default'} size="sm" dot>
                        {form.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                    {form.description && (
                      <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                        {form.description}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" onClick={() => setPreviewFormId(form.id)}>
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigate(`/settings/form-builder/${form.id}`)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        const newName = window.prompt(
                          'Enter name for the copy:',
                          `${form.name} (Copy)`,
                        );
                        if (newName) {
                          copyMutation.mutate(
                            { id: form.id, name: newName },
                            {
                              onSuccess: (result) => {
                                toast({ variant: 'success', title: 'Form copied' });
                                navigate(`/settings/form-builder/${result.id}`);
                              },
                            },
                          );
                        }
                      }}
                    >
                      Copy
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(form)}>
                      Delete
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {isCreateOpen && (
        <CreateFormModal
          onSave={handleCreate}
          onClose={() => setIsCreateOpen(false)}
          isSaving={createMutation.isPending}
        />
      )}
      {/* Form Preview Modal */}
      {previewFormId && (
        <FormPreviewModal formId={previewFormId} onClose={() => setPreviewFormId(null)} />
      )}
    </div>
  );
}

// ─── Form Preview Modal ──────────────────────────────────────────────────────

function FormPreviewModal({ formId, onClose }: { formId: string; onClose: () => void }) {
  const { data: form, isLoading } = useFormDefinitionFull(formId);
  // Validate on blur so the preview demonstrates field formats (regex) live.
  const previewMethods = useForm({ mode: 'onBlur' });

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
        <div className="rounded-xl bg-white p-8 dark:bg-gray-900">
          <div className="h-6 w-6 animate-spin rounded-full border-4 border-brand-600 border-t-transparent" />
        </div>
      </div>
    );
  }

  if (!form) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <div className="rounded-xl bg-white p-6 dark:bg-gray-900">
          <p className="text-sm text-gray-500">Form not found.</p>
          <Button type="button" variant="outline" onClick={onClose} className="mt-4">
            Close
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-4xl max-h-[90vh] overflow-y-auto rounded-xl bg-white p-6 shadow-xl dark:bg-gray-900">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Preview: {form.name}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              This is how the form will appear to users
            </p>
          </div>
          <Button type="button" variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
        <FormProvider {...previewMethods}>
          <ListingFormPreview formDefinition={form} />
        </FormProvider>
      </div>
    </div>
  );
}

function ListingFormPreview({ formDefinition }: { formDefinition: FormDefinitionFull }) {
  const sections = formDefinition.sections ?? [];
  const tabSections = sections.filter((s) => s.render_as === 'tab' && !s.parent_tab_id);
  const orphanSections = sections.filter(
    (s) => s.render_as !== 'tab' && !s.parent_tab_id && s.is_active,
  );

  const firstTabId = tabSections.length > 0 ? `section-${tabSections[0].id}` : 'details';
  const { activeTab, onChange } = useTabs(firstTabId);

  const tabs =
    tabSections.length > 0
      ? tabSections.map((s) => ({ id: `section-${s.id}`, label: s.name }))
      : orphanSections.length > 0
        ? [{ id: 'details', label: 'Details' }]
        : [];

  if (tabs.length === 0) {
    return <p className="py-8 text-center text-sm text-gray-500">No sections to preview.</p>;
  }

  return (
    <div className="space-y-4">
      <Tabs tabs={tabs} activeTab={activeTab} onChange={onChange} />
      {tabSections.map((tab, tabIndex) => {
        const explicitChildren = sections
          .filter((s) => s.parent_tab_id === tab.id && s.render_as !== 'tab' && s.is_active)
          .sort((a, b) => a.display_order - b.display_order);
        let implicitChildren: typeof sections = [];
        if (explicitChildren.length === 0) {
          const nextTab = tabSections[tabIndex + 1];
          implicitChildren = orphanSections.filter((s) => {
            const afterThisTab = s.display_order >= tab.display_order;
            const beforeNextTab = nextTab ? s.display_order < nextTab.display_order : true;
            return afterThisTab && beforeNextTab;
          });
        }
        const children = explicitChildren.length > 0 ? explicitChildren : implicitChildren;
        const tabHasFields = tab.fields.filter((f) => f.is_active).length > 0;
        const sectionsToRender = [
          ...(tabHasFields ? [{ ...tab, render_as: 'section' as const }] : []),
          ...children,
        ];
        return (
          <TabPanel key={tab.id} tabId={`section-${tab.id}`} activeTab={activeTab}>
            <DynamicFormRenderer
              formDefinition={{ ...formDefinition, sections: sectionsToRender }}
            />
          </TabPanel>
        );
      })}
      {tabSections.length === 0 && orphanSections.length > 0 && (
        <TabPanel tabId="details" activeTab={activeTab}>
          <DynamicFormRenderer formDefinition={{ ...formDefinition, sections: orphanSections }} />
        </TabPanel>
      )}
    </div>
  );
}

// ─── Create Form Modal ───────────────────────────────────────────────────────

function CreateFormModal({
  onSave,
  onClose,
  isSaving,
}: {
  onSave: (data: { name: string; description: string; entity_scope: EntityScope }) => void;
  onClose: () => void;
  isSaving: boolean;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [entityScope, setEntityScope] = useState<EntityScope>('contact');

  return (
    <Modal open onClose={onClose} title="New Form Definition" size="md">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ name, description, entity_scope: entityScope });
        }}
        className="space-y-4"
      >
        <Input
          label="Form Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          placeholder="e.g., Individual Contact Form"
        />
        <Input
          label="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description"
        />
        <Select
          label="Entity Scope"
          value={entityScope}
          onChange={(e) => setEntityScope(e.target.value as EntityScope)}
          options={Object.entries(SCOPE_LABELS).map(([v, l]) => ({ value: v, label: l }))}
        />
        <div className="flex justify-end gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" loading={isSaving} disabled={!name.trim()}>
            Create
          </Button>
        </div>
      </form>
    </Modal>
  );
}
